import { formatInTimeZone } from "date-fns-tz";

/**
 * Formats an IANA timezone name to be more readable by replacing underscores with spaces
 * and adding spaces around slashes.
 */
export const formatIanaTimezone = (timezone: string) => {
  return timezone.replace(/_/g, " ").replace(/\//g, " / ");
};

/**
 * Gets the GMT offset using formatInTimeZone from date-fns-tz
 */
function getGmtOffsetWithDateFns(timeZone: string): string {
  if (timeZone === "UTC") {
    return "GMT±00:00";
  }

  return formatInTimeZone(new Date(), timeZone, "'GMT'xxx");
}

/**
 * Gets the GMT offset using native toLocaleString
 */
export function getGmtOffset(timeZone: string): string {
  if (timeZone === "UTC") {
    return "GMT±00:00";
  }

  const date = new Date();
  const gmtOffset = date
    .toLocaleString("en-US", {
      timeZone,
      timeZoneName: "longOffset",
    })
    .split(" ")
    .pop();

  return gmtOffset || "GMT";
}

/**
 * Gets the timezone abbreviation (like PST, EST, etc.)
 */
function getTimezoneAbbreviation(timeZone: string): string {
  if (timeZone === "UTC") {
    return "UTC";
  }

  const date = new Date();
  const abbreviation = date
    .toLocaleString("en-US", {
      timeZone,
      timeZoneName: "short",
    })
    .split(" ")
    .pop();

  return abbreviation || timeZone;
}

/**
 * Formats a timezone with full name and GMT offset
 * (e.g., "America / Los Angeles (GMT-08:00)")
 */
export function formatTimezoneDetailed(timezone: string): string {
  if (timezone === "UTC") {
    return "UTC (GMT±00:00)";
  }

  const formattedTimezone = formatIanaTimezone(timezone);
  let gmtPart = getGmtOffsetWithDateFns(timezone);

  // Replace GMT+0:00 or GMT-0:00 with GMT±0:00 for better readability
  gmtPart = gmtPart.replace(/GMT[+-]00:00/g, "GMT±00:00");

  return `${formattedTimezone} (${gmtPart})`;
}

/**
 * Formats a timezone with abbreviation and GMT offset
 * (e.g., "PST (GMT-08:00)")
 */
export function formatTimezoneCompact(timeZone: string): string {
  if (timeZone === "UTC") {
    return "UTC (GMT±00:00)";
  }

  const abbreviation = getTimezoneAbbreviation(timeZone);
  const gmtOffset = getGmtOffset(timeZone);

  return `${abbreviation} (${gmtOffset})`;
}
